import React, { FC, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Link } from 'react-router-dom';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { AppRoutes } from '@/routes';
import { useLoadPullingSummary } from '@/api/OperationsDashboard';
import { LaneRisk } from '@/api/OperationsDashboard/types';
import {
  LOAD_LANE_STATUS_CONFIG,
  LoadLaneStatus,
} from '@/modules/loadlanes/constants';
import {
  formatLaneNumber,
  getProviderInitials,
} from '@/modules/loadlanes/utils';
import { ChartCard } from './ChartCard';

dayjs.extend(utc);

const HEIGHT = 300;

const PILL_ORDER: LoadLaneStatus[] = [
  'overdue',
  'dueSoon',
  'onTime',
  'complete',
  'vacant',
];

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export const LoadPullingOverview: FC = () => {
  const { t } = useTranslate();
  const { userPermissions } = useAppCustomers();

  const availableLocations = useMemo(
    () => userPermissions.locations ?? [],
    [userPermissions.locations],
  );

  const [siteId, setSiteId] = useState<number>();

  useEffect(() => {
    if (availableLocations.length && !siteId) {
      setSiteId(availableLocations[0].locationId);
    }
  }, [availableLocations, siteId]);

  const { data, isFetching, isError, refetch } = useLoadPullingSummary(siteId);

  const riskLabel = (risk: LaneRisk) => {
    const cfg = LOAD_LANE_STATUS_CONFIG[risk.status as LoadLaneStatus];
    if (!risk.eta) return cfg.label;
    const eta = dayjs.utc(risk.eta).local();
    const diffMinutes = eta.diff(dayjs(), 'minute');
    if (risk.status === 'overdue') {
      return t('dashboard.loadPulling.overdueBy', {
        duration: formatDuration(Math.abs(diffMinutes)),
      });
    }
    if (diffMinutes <= 0) return t('dashboard.loadPulling.dueNow');
    return t('dashboard.loadPulling.dueIn', {
      duration: formatDuration(diffMinutes),
    });
  };

  const selectors = (
    <Select
      size="small"
      style={{ minWidth: 180 }}
      value={siteId}
      placeholder={t('dashboard.loadPulling.selectSite')}
      options={availableLocations.map((location) => ({
        label: location.locationName,
        value: location.locationId,
      }))}
      onChange={setSiteId}
    />
  );

  const laneCount = data?.laneCount ?? 0;
  const atRiskCount = data?.atRiskCount ?? 0;
  const rows = data?.needsAttention ?? [];
  const moreCount = Math.max(0, atRiskCount - rows.length);

  return (
    <ChartCard
      title={t('dashboard.widget.loadPulling')}
      extra={selectors}
      isLoading={isFetching}
      isError={isError}
      isEmpty={!!data && data.laneCount === 0}
      onRetry={() => refetch()}
      height={HEIGHT}
    >
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}
      >
        {PILL_ORDER.map((status) => {
          const cfg = LOAD_LANE_STATUS_CONFIG[status];
          return (
            <span
              key={status}
              style={{
                background: cfg.tabBg,
                color: cfg.color,
                border: `1px solid ${cfg.border}`,
                padding: '5px 12px',
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {data?.[status] ?? 0} {cfg.label}
            </span>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 12,
        }}
      >
        <div>
          <span style={{ color: '#041b53', fontSize: 22, fontWeight: 600 }}>
            {(data?.pulledVehicles ?? 0).toLocaleString()}
          </span>
          <span style={{ color: '#838383', fontSize: 13 }}>
            {' / '}
            {(data?.plannedVehicles ?? 0).toLocaleString()}{' '}
            {t('dashboard.loadPulling.planned')}
          </span>
          <div style={{ color: '#bdc3c7', fontSize: 12, marginTop: 2 }}>
            {t('dashboard.loadPulling.vehiclesPulled')} · {laneCount}{' '}
            {t('dashboard.loadPulling.lanes')}
          </div>
        </div>
        <Link
          to={AppRoutes.loadlanes}
          style={{ color: '#1677ff', fontSize: 13, fontWeight: 500 }}
        >
          {t('dashboard.loadPulling.viewAll', { count: laneCount })}
        </Link>
      </div>

      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          paddingTop: 10,
        }}
      >
        <div
          style={{
            color: '#8c8c8c',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          {t('dashboard.loadPulling.needsAttention')}
        </div>

        {rows.length === 0 ? (
          <div style={{ color: '#37B24D', fontSize: 13, padding: '6px 0' }}>
            {t('dashboard.loadPulling.allClear')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((risk) => {
              const cfg =
                LOAD_LANE_STATUS_CONFIG[risk.status as LoadLaneStatus];
              return (
                <div
                  key={risk.lane}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      minWidth: 30,
                      height: 24,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: cfg.tabBg,
                      color: cfg.color,
                      border: `1px solid ${cfg.border}`,
                      borderRadius: 6,
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {formatLaneNumber(risk.lane)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: '#1f1f1f',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {risk.loadRef || '—'}
                    </div>
                    <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                      {getProviderInitials(risk.carrier)}
                    </div>
                  </div>
                  <span style={{ color: cfg.color, fontWeight: 500 }}>
                    {riskLabel(risk)}
                  </span>
                  <span
                    style={{
                      minWidth: 44,
                      textAlign: 'right',
                      color: '#595959',
                    }}
                  >
                    {risk.pulled}/{risk.total}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {atRiskCount > 0 ? (
          <div style={{ color: '#bdc3c7', fontSize: 12, marginTop: 10 }}>
            {moreCount > 0
              ? `${t('dashboard.loadPulling.moreAtRisk', {
                  count: moreCount,
                })} · `
              : ''}
            {t('dashboard.loadPulling.atRiskSummary', {
              atRisk: atRiskCount,
              total: laneCount,
            })}
          </div>
        ) : null}
      </div>
    </ChartCard>
  );
};
